import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { MessageCircle } from 'lucide-react';

interface FeedbackDialogProps {
    user: User | null;
    API_BASE_URL: string;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ user, API_BASE_URL }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [elementData, setElementData] = useState<any>(null);
    const [type, setType] = useState<'feedback' | 'hazard' | 'map_element'>('feedback');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const handleOpen = (e: any) => {
            const { element, type, defaultDescription } = e.detail;
            setElementData(element);
            setType(type || 'feedback');
            setDescription(defaultDescription || '');
            setIsOpen(true);
        };

        window.addEventListener('open-feedback-dialog', handleOpen);
        return () => window.removeEventListener('open-feedback-dialog', handleOpen);
    }, []);

    if (!isOpen || !elementData) return null;

    const handleSubmit = async () => {
        if (!user) {
            alert("Du musst dich einloggen, um Feedback zu geben.");
            return;
        }
        if (!description.trim()) {
            alert("Bitte gib eine Fehlerbeschreibung oder ein Feedback ein.");
            return;
        }

        setIsSubmitting(true);
        const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');

        try {
            const res = await fetch(`${API_BASE_URL}/tickets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    type: type,
                    title: `[Karte] ${elementData.title || elementData.name || 'Kartenlement'} – Feedback`,
                    description: description,
                    ref_id: String(elementData.id || 'unknown'),
                    context_data: {
                        element: elementData,
                        type,
                        url: window.location.pathname
                    }
                })
            });

            if (res.ok) {
                alert("Dein Feedback wurde ans Admin-Team gesendet. Vielen Dank!");
                setIsOpen(false);
                setDescription('');
            } else {
                const err = await res.text();
                console.error('Feedback error:', res.status, err);
                alert("Fehler beim Senden. Bitte später erneut versuchen.");
            }
        } catch (err) {
            console.error(err);
            alert("Netzwerkfehler.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
                
                {/* Close Button */}
                <button 
                    onClick={() => setIsOpen(false)}
                    className="absolute top-5 right-5 w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                    title="Schließen"
                >
                    ✕
                </button>

                <h3 className="text-xl font-bold tracking-tight text-white mb-1 flex items-center gap-2">
                    {type === 'hazard' ? '⚠️ Gefahr melden' : <><MessageCircle className="w-4 h-4 inline mr-1" /> Element Feedback</>}
                </h3>
                <p className="text-sm text-slate-400 mb-6">
                    Bezieht sich auf: <strong className="text-sky-400">{elementData.title || elementData.name || 'Ausgewählt'}</strong>
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                            Art des Feedbacks
                        </label>
                        <select 
                            title="Art des Feedbacks"
                            aria-label="Art des Feedbacks"
                            value={type} 
                            onChange={(e) => setType(e.target.value as any)}
                            className="w-full bg-black/40 border border-white/5 rounded-lg p-3 text-white text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all outline-none"
                        >
                            <option value="feedback">Fehlerhafte Daten (Koordinaten, Name...)</option>
                            <option value="map_element">Allgemeines Feedback / Vorschlag</option>
                            <option value="hazard">Gefahr / Risiko an diesem Ort melden</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                            Beschreibung
                        </label>
                        <textarea 
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Beschreibe kurz das Problem oder deinen Vorschlag..."
                            className="w-full bg-black/40 border border-white/5 rounded-lg p-3 text-white text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all outline-none resize-none custom-scrollbar"
                        />
                    </div>
                </div>

                <div className="flex gap-3 mt-6 pt-6 border-t border-white/5">
                    <button 
                        onClick={() => setIsOpen(false)}
                        className="flex-1 py-2.5 rounded-lg font-bold text-sm bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                        Abbrechen
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1 py-2.5 rounded-lg font-bold text-sm bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/50 transition-all disabled:opacity-50"
                    >
                        {isSubmitting ? 'Wird gesendet...' : 'Ticket absenden'}
                    </button>
                </div>
            </div>
        </div>
    );
};
